import {
  currentGlobexStatus,
  currentNyseStatus,
  formatDuration,
  status,
  tradingSessions,
} from "../lib/session-math.js";
import { storageGet, storageSet } from "../lib/storage.js";
import { el, svgIcon, spacer, divider } from "../lib/dom.js";

type ClockMode = "forex" | "stocks";
const MODE_KEY = "clockPageMode";

const zones: Array<[string, string]> = [
  ["Local", Intl.DateTimeFormat().resolvedOptions().timeZone],
  ["New York", "America/New_York"],
  ["London", "Europe/London"],
  ["Tokyo", "Asia/Tokyo"],
];

const iconPaths = {
  house: "M2.5 8L8 3l5.5 5M4 7v6h8V7M6.5 13v-3h3v3",
  lockClosed: "M5.5 7.5V5.5a2.5 2.5 0 0 1 5 0v2M4 7.5h8V13H4zM8 9.5v1.5",
  lockOpen: "M5.5 7.5V5a2.5 2.5 0 0 1 4.9-.7M4 7.5h8V13H4zM8 9.5v1.5",
};

/** Hover tag: the human name of the timezone a row is showing. */
function zoneTooltip(timeZone: string, now: Date): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "long" }).formatToParts(now);
    const long = parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
    const shortParts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(now);
    const short = shortParts.find((p) => p.type === "timeZoneName")?.value ?? "";
    return short && short !== long ? `${long} (${short})` : long;
  } catch {
    return timeZone;
  }
}

const sessionZones: Record<string, string> = {
  "Asia (Tokyo)": "Asia/Tokyo",
  London: "Europe/London",
  "New York": "America/New_York",
};

/** The round badge: text glyph for currencies, line icon for house/locks —
 * all icon-styled rather than pictorial, in a neutral gray unless state
 * (session active, market open/closed) carries meaning. */
function badge(content: string | SVGElement, background: string): HTMLElement {
  const node = el("div", { className: "icon-badge", style: { background } });
  if (typeof content === "string") {
    node.textContent = content;
    node.style.font = "600 12px -apple-system, 'Segoe UI', sans-serif";
  } else {
    node.appendChild(content);
  }
  return node;
}

function regionBadgeContent(name: string): string | SVGElement {
  switch (name) {
    case "Local":
      return svgIcon(iconPaths.house, 13);
    case "New York":
      return "$";
    case "London":
      return "£";
    case "Tokyo":
    case "Asia (Tokyo)":
      return "¥";
    default:
      return "•";
  }
}

function clockRow(city: string, timeZone: string, now: Date): HTMLElement {
  const row = el("div", { className: "row", title: zoneTooltip(timeZone, now) });
  row.appendChild(badge(regionBadgeContent(city), "#8e8e93"));
  row.appendChild(el("div", { text: city, style: { font: "var(--font-body)" } }));
  row.appendChild(spacer());

  const hereDay = new Intl.DateTimeFormat("en-US", { day: "numeric" }).format(now);
  const zoneDay = new Intl.DateTimeFormat("en-US", { day: "numeric", timeZone }).format(now);
  if (hereDay !== zoneDay) {
    row.appendChild(
      el("div", {
        text: new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone }).format(now),
        style: { font: "var(--font-caption)", color: "var(--accent-orange)", marginRight: "6px" },
      })
    );
  }

  row.appendChild(
    el("div", {
      text: new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone,
      }).format(now),
      style: { font: "var(--font-mono-value)", fontWeight: "400", color: "var(--text-secondary)" },
    })
  );
  return row;
}

// A titled card matching the rest of Monitor (System overview / Deploy): an
// uppercase section label above a bordered, elevated card holding the rows.
function labeledCard(title: string, rows: HTMLElement[]): HTMLElement {
  return el("div", { style: { display: "flex", flexDirection: "column", gap: "6px" } }, [
    el("div", { text: title, style: { font: "var(--font-label)", color: "var(--text-secondary)", letterSpacing: "0.06em", textTransform: "uppercase", padding: "0 2px" } }),
    el("div", { style: { background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "12px", padding: "8px 12px", display: "flex", flexDirection: "column", gap: "8px" } }, rows),
  ]);
}

function sessionRow(s: ReturnType<typeof status>, now: Date): HTMLElement {
  const tz = sessionZones[s.session.name];
  const wrapper = el("div", { style: { display: "flex", flexDirection: "column", gap: "3px" } });
  const row = el("div", { className: "row", title: tz ? zoneTooltip(tz, now) : undefined });
  // Active vs idle reads by luminance (solid vs faded neutral) + bold label +
  // the progress track below — never hue (D-075: no green/red as status).
  row.appendChild(badge(regionBadgeContent(s.session.name), s.isActive ? "#8e8e93" : "rgba(142,142,147,0.4)"));
  row.appendChild(
    el("div", { text: s.session.name, style: { font: "var(--font-body)", fontWeight: s.isActive ? "600" : "400", color: s.isActive ? "var(--text-primary)" : "var(--text-secondary)" } })
  );
  row.appendChild(spacer());
  row.appendChild(
    el("div", {
      text: (s.isActive ? "ends in " : "starts in ") + formatDuration(s.minutesRemaining),
      style: { font: "var(--font-caption)", color: s.isActive ? "var(--text-primary)" : "var(--text-secondary)" },
    })
  );
  wrapper.appendChild(row);
  // Active sessions get a subtle elapsed/remaining track (audit: UX by page).
  if (s.isActive) {
    const startM = s.session.startHourET * 60;
    let endM = s.session.endHourET * 60;
    if (endM <= startM) endM += 24 * 60;
    const totalMinutes = endM - startM;
    const fraction = Math.min(1, Math.max(0, 1 - s.minutesRemaining / totalMinutes));
    const track = el("div", { style: { height: "2px", borderRadius: "1px", background: "var(--bg-fill-strong)", marginLeft: "34px", overflow: "hidden" } });
    track.appendChild(el("div", { style: { height: "100%", width: `${(fraction * 100).toFixed(1)}%`, background: "var(--text-secondary)" } }));
    wrapper.appendChild(track);
  }
  return wrapper;
}

function marketRow(isOpen: boolean, title: string, minutesRemaining: number, tooltip: string): HTMLElement {
  const row = el("div", { className: "row", title: tooltip });
  // Open vs closed reads by the lock icon's shape + luminance, not green/red (D-075).
  row.appendChild(badge(svgIcon(isOpen ? iconPaths.lockOpen : iconPaths.lockClosed, 13), isOpen ? "#8e8e93" : "rgba(142,142,147,0.4)"));
  row.appendChild(el("div", { text: title, style: { font: "var(--font-body)", fontWeight: "600" } }));
  row.appendChild(spacer());
  row.appendChild(
    el("div", {
      text: (isOpen ? "closes in " : "opens in ") + formatDuration(minutesRemaining),
      style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
    })
  );
  return row;
}

/** Clocks + sessions + market hours. With `opts.mode` the market is fixed and
 * the internal picker is hidden (Monitor chooses via its toolbar — D-085);
 * without it, the footer picker toggles Forex&Commodities / Stocks. */
export async function renderClockContent(root: HTMLElement, opts: { mode?: ClockMode } = {}): Promise<() => void> {
  const fixed = opts.mode;
  let mode: ClockMode = fixed ?? (await storageGet<ClockMode>(MODE_KEY, "forex"));

  // Content scrolls in the middle; the mode picker (when shown) is pinned to the
  // bottom so its buttons stay put when the content above changes.
  const container = el("div", { style: { display: "flex", flexDirection: "column", gap: "12px", height: "100%" } });
  const content = el("div", { className: "scroll", style: { display: "flex", flexDirection: "column", gap: "12px" } });
  const footer = el("div", { style: { flex: "none", display: "flex", flexDirection: "column", gap: "6px" } });
  container.appendChild(content);
  if (!fixed) container.appendChild(footer);
  root.replaceChildren(container);

  function renderFooter() {
    footer.replaceChildren();
    footer.appendChild(divider());
    footer.appendChild(
      el("div", { text: "Show hours for", style: { font: "var(--font-caption)", color: "var(--text-secondary)" } })
    );
    const picker = el("div", { style: { display: "flex", gap: "4px" } });
    const options: Array<[ClockMode, string]> = [
      ["forex", "Forex & Commodities"],
      ["stocks", "Stocks"],
    ];
    for (const [value, label] of options) {
      picker.appendChild(
        el("button", {
          className: "pill" + (mode === value ? " selected" : ""),
          text: label,
          style: { flex: "1", justifyContent: "center" },
          onClick: () => {
            mode = value;
            void storageSet(MODE_KEY, mode);
            renderFooter();
            tick();
          },
        })
      );
    }
    footer.appendChild(picker);
  }

  function tick() {
    const now = new Date();
    content.replaceChildren();

    content.appendChild(labeledCard("Current Time", zones.map(([city, tz]) => clockRow(city, tz, now))));

    if (mode === "forex") {
      content.appendChild(labeledCard("Trading Sessions", tradingSessions.map((session) => sessionRow(status(session, now), now))));

      const globex = currentGlobexStatus(now);
      content.appendChild(labeledCard("CME Globex Hours", [
        marketRow(
          globex.isOpen,
          globex.note ? `${globex.label} · holiday hours` : globex.label,
          globex.minutesRemaining,
          globex.note ?? "CME Globex schedule (US Central Time exchange, shown against Eastern Time)"
        ),
      ]));
    } else {
      const nyse = currentNyseStatus(now);
      content.appendChild(labeledCard("Market Hours", [
        marketRow(nyse.isOpen, nyse.isOpen ? "NYSE Open" : "NYSE Closed", nyse.minutesRemaining, zoneTooltip("America/New_York", now)),
      ]));
    }
  }

  if (!fixed) renderFooter();
  tick();
  const interval = setInterval(tick, 1000);
  return () => clearInterval(interval);
}
